import { TaskChecklistEditor } from '../../../components/ui/task-checklist-editor';
import { EstimateCard } from '../../ai/components/estimate-card';
import { PartialSuggestionNotice } from '../../ai/components/partial-suggestion-notice';
import { TaskDependencyEditor } from './task-dependency-editor';
import { randomUuid } from '../../../lib/uuid';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { ListChecks, Wand2 } from 'lucide-react';
import { type ReactNode, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import {
  type Permission,
  type TaskKind,
  type AiSuggestionView,
  type TaskView,
  type ChecklistItemInput,
  ChecklistItemsInput,
  BreakdownOutput,
  TaskPriority,
  checkTaskStandards,
  hoursFromRates,
} from '@apm/contracts';
import { bucketOf } from '@apm/estimate';
import { RichTextEditor } from '../../../components/ui/rich-text-editor';
import { useDiscardConfirm } from '../../../components/ui/discard-changes-confirm';
import { Button } from '../../../components/ui/button';
import { errorMessage } from '../../../lib/error-message';
import { useTrackedAiRun } from '../../ai/use-ai-run';
import { useAiRunConfirm } from '../../ai/components/ai-run-confirm';
import { announceCreated } from '../card-motion';
import { taskFormApi, taskFormKeys } from '../api';
import { DatePicker } from '../../../components/ui/date-picker';
import { AssigneePicker } from './assignee-picker';
import { TriageStrip } from './triage-strip';
import { TaskFormAiAssist } from './task-form-ai-assist';
import { dashboardApi, dashboardKeys } from '../../dashboard/api';
import { GlassIconButton } from '../../../components/ui/glass-icon-button';

/**
 * Form tạo / sửa task — APM-1308b.
 *
 * ## ⚠️ Vì sao cảnh báo phải hiện NGAY LÚC GÕ
 *
 * `APM-1308a` đã kiểm ở API và trả `warnings[]`. Nhưng một cảnh báo chỉ đến sau
 * khi bấm lưu thì người dùng đã gõ xong, đã bấm, và giờ phải quay lại sửa —
 * trong khi thứ họ cần là biết ngay lúc gõ số 2 vào ô ước lượng.
 *
 * Nên form gọi `checkTaskStandards` — **đúng hàm** mà `AssignmentWarningService`
 * gọi (`@apm/contracts`). Viết lại luật ở đây là cách chắc chắn nhất để hai bên
 * lệch nhau đúng vào hôm admin đổi trần, và bên lệch sẽ là bên người dùng thấy.
 *
 * Ba cảnh báo còn lại (quá tải, quá nhiều người, vượt WIP) cần dữ liệu mà form
 * không có và **không nên** có — chúng vẫn tới đủ trong `warnings[]` sau khi lưu.
 *
 * ## Vì sao "AI tách giúp" chỉ có ở chế độ sửa
 *
 * `BreakdownSkill` đọc một task đang tồn tại qua `get_task`. Bấm nút ấy trên
 * một task chưa lưu thì không có gì để đọc — và tạo một task nháp chỉ để AI có
 * cái đọc là để lại rác khi người dùng đóng form.
 */
interface TaskFormDialogProps {
  readonly projectId: string;
  /** Có = sửa task này. Không có = tạo mới. */
  readonly task?: TaskView | null;
  /** Cột đích khi tạo từ một cột board cụ thể. */
  readonly statusId?: string | null;
  /** `ai_suggestion:approve` — thiếu thì vẫn xem được gợi ý, chỉ không duyệt. */
  readonly canApprove?: boolean;
  /**
   * `task:assign` — mở ô "Người làm".
   *
   * Quyền riêng, không suy từ quyền sửa việc: CONTRIBUTOR sửa được việc của
   * mình nhưng không giao việc cho ai, nên gộp hai thứ sẽ hiện một ô mà
   * `PATCH /tasks/:id/assignee` luôn trả 403.
   *
   * **Bỏ trống ⇒ form tự hỏi** `task:assign` của đúng dự án này. Bản trước mặc
   * định `false`, nên Board, Gantt và popup ở "Việc của tôi" — ba chỗ không
   * truyền prop — giấu ô "Người làm" kể cả với PM: người dùng báo 2026-09-25
   * "tạo việc mới chưa có phần gán người làm, sửa task cũng không đổi được".
   */
  readonly canAssign?: boolean;
  readonly onClose: () => void;
  readonly onSaved?: (task: TaskView) => void;
}

export function TaskFormDialog({
  projectId,
  task = null,
  statusId = null,
  canApprove = false,
  canAssign: canAssignProp,
  onClose,
  onSaved,
}: TaskFormDialogProps): ReactNode {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const track = useTrackedAiRun();
  const aiConfirm = useAiRunConfirm(projectId);
  const editing = task !== null;

  const abilities = useQuery({
    queryKey: dashboardKeys.abilities,
    queryFn: dashboardApi.abilities,
    staleTime: 5 * 60 * 1000,
    /*
      Luôn hỏi — không chỉ khi thiếu `canAssign`: `project:update` quyết hai thứ
      mà chỗ gọi không truyền (khoá ô ngày khi sửa, ô tích "Dùng AI giao việc"
      khi tạo). Cùng khoá cache với dashboard nên gần như luôn có sẵn.
    */
  });
  const has = (permission: Permission): boolean =>
    abilities.data !== undefined &&
    (abilities.data.global.includes(permission) ||
      (abilities.data.perProject
        .find((entry) => entry.projectId === projectId)
        ?.permissions.includes(permission) ??
        false));
  const canAssign = canAssignProp ?? has('task:assign');
  /**
   * Sửa ưu tiên và ngày của một việc **đã có** là lập kế hoạch — `project:update`
   * (PM), cùng cửa `assertMaySchedule` ở API. Lúc **tạo** thì không khoá: người
   * tạo việc của chính mình vẫn đặt được ưu tiên và hạn ban đầu.
   *
   * **Ngày** mở theo cài đặt của dự án từ APM-TSCH-b: server gắn `canSchedule` lên từng việc
   * (`taskScheduleRules`, cùng luật với cửa ở API). Ưu tiên vẫn chỉ PM.
   */
  const priorityLocked = editing && !has('project:update');
  const scheduleLocked = priorityLocked && task?.canSchedule !== true;
  /**
   * Ô tích "Dùng AI giao việc" — APM-AIA-d. Chỉ PM: Auto Assign đặt người
   * (`task:assign`) **và** ngày (`project:update`); người dùng chốt 2026-09-25.
   */
  const canPlanWithAi = canAssign && has('project:update');

  const [title, setTitle] = useState(task?.title ?? '');
  const [description, setDescription] = useState(task?.description ?? '');
  /*
    `phase_gate` không có trong ô chọn: nó là cổng giữa hai pha của Waterfall,
    do luồng lập kế hoạch tạo ra kèm điều kiện qua cổng — không phải thứ người
    dùng bật lên từ form sửa một task. Task đang là `phase_gate` mà mở form thì
    ô rơi về "Việc thường"; đổi giá trị mới ghi đè, còn bấm Lưu mà không đụng
    tới ô này thì `kind` không nằm trong payload.
  */
  const [kind, setKind] = useState<TaskKind>(task?.kind === 'milestone' ? 'milestone' : 'task');
  const [checklist, setChecklist] = useState<ChecklistItemInput[] | null>(
    task?.checklist?.items.map(({ id, text, weight }) => ({ id, text, weight })) ?? null,
  );
  // Keep the revision paired with the draft, even if background queries refresh the task prop.
  const [checklistVersion] = useState(task?.checklistVersion ?? 0);
  const [checklistDirty, setChecklistDirty] = useState(false);
  const checklistValid = checklist === null || ChecklistItemsInput.safeParse(checklist).success;
  const changeChecklist = (items: ChecklistItemInput[] | null) => {
    setChecklist(items);
    setChecklistDirty(true);
  };
  const [taskTypeId, setTaskTypeId] = useState(task?.taskTypeId ?? '');
  const [assigneeId, setAssigneeId] = useState(task?.assigneeId ?? '');
  const [priority, setPriority] = useState(task?.priority ?? 'medium');
  const [estimate, setEstimate] = useState(
    task?.estimateHours === null || task?.estimateHours === undefined
      ? ''
      : String(task.estimateHours),
  );
  const [storyPoints, setStoryPoints] = useState(
    task?.storyPoints === null || task?.storyPoints === undefined ? '' : String(task.storyPoints),
  );
  const [aiAssist, setAiAssist] = useState(false);
  /**
   * Người làm do **Auto Assign** điền khi đang sửa một việc có sẵn.
   *
   * Chọn tay ở ô Người làm thì gọi `PATCH /assignee` ngay (APM-TASK-assign). Gợi
   * ý của AI thì **không** — nó chỉ thành sự thật khi người bấm Lưu (luật #6),
   * nên cờ này bảo `save` gọi đường giao việc trước khi lưu phần còn lại.
   */
  const [assigneeFromAi, setAssigneeFromAi] = useState(false);
  const [startDate, setStartDate] = useState(task?.startDate ?? '');
  const [dueDate, setDueDate] = useState(task?.dueDate ?? '');
  const [suggestion, setSuggestion] = useState<AiSuggestionView | null>(null);
  const [chosen, setChosen] = useState<ReadonlySet<number>>(new Set());

  const standards = useQuery({
    queryKey: taskFormKeys.standards(projectId),
    queryFn: () => taskFormApi.standards(projectId),
  });
  const types = useQuery({
    queryKey: taskFormKeys.taskTypes(projectId),
    queryFn: () => taskFormApi.taskTypes(projectId),
  });
  /*
    Giờ cho 1 SP của từng thành viên — gõ SP thì ô "Thời gian làm" tự đổi theo tốc độ người
    được giao, sửa tay được (người dùng yêu cầu 2026-11-02). Server chỉ trả con số; tên bậc
    chỉ có với người quản lý được giá thuê / tốc độ.
  */
  const storyPointRates = useQuery({
    queryKey: taskFormKeys.storyPointRates(projectId),
    queryFn: () => taskFormApi.storyPointRates(projectId),
  });
  /** Người dùng đã tự gõ giờ (hoặc AI đã điền) ⇒ thôi tự đổi giờ theo SP. */
  const [estimateTouched, setEstimateTouched] = useState(false);
  const autoEstimate = (pointsText: string, userId: string): string | null => {
    const sp = Number(pointsText);
    const data = storyPointRates.data;
    if (!data || pointsText.trim() === '' || !Number.isFinite(sp) || sp <= 0) return null;
    const member = data.members.find((row) => row.userId === userId);
    return member
      ? String(hoursFromRates(sp, member.rates, bucketOf(sp * data.anchor.hours)))
      : null;
  };
  const changeStoryPoints = (value: string): void => {
    setStoryPoints(value);
    if (estimateTouched) return;
    const next = autoEstimate(value, assigneeId);
    if (next !== null) setEstimate(next);
  };
  const assigneeRates = storyPointRates.data?.members.find((row) => row.userId === assigneeId);
  const spEntered = storyPoints.trim() !== '' && Number(storyPoints) > 0;
  const estimateHint =
    kind === 'milestone' || !spEntered || estimateTouched || !storyPointRates.data
      ? null
      : assigneeId === ''
        ? t('taskPlanGate.dialog.hoursAfterAssign')
        : !assigneeRates
          ? null
          : assigneeRates.level === undefined
            ? t('taskPlanGate.dialog.hoursAutoNoLevel')
            : t('taskPlanGate.dialog.hoursAuto', {
                level: assigneeRates.level
                  ? t(`levelRate.level.${assigneeRates.level}`)
                  : t('taskPlanGate.dialog.levelStandard'),
              });

  const members = useQuery({
    queryKey: taskFormKeys.members(projectId),
    queryFn: () => taskFormApi.members(projectId),
  });

  // Loại việc mặc định là loại đầu tiên: mọi task phải có loại, và bắt người
  // dùng chọn trước khi gõ tiêu đề là dựng một bậc thềm ở đúng bước đầu.
  useEffect(() => {
    if (taskTypeId === '' && types.data && types.data.length > 0) {
      setTaskTypeId(types.data[0]!.id);
    }
  }, [types.data, taskTypeId]);

  /**
   * Còn thứ gì người dùng đã gõ mà chưa lưu không — để hỏi trước khi đóng.
   *
   * So với giá trị **lúc mở form**, không với một cờ "đã chạm": gõ rồi xoá về
   * như cũ thì không có gì để mất. Loại việc so với đúng giá trị mà effect phía
   * trên tự điền (loại đầu tiên) — không thì form vừa mở đã "có thay đổi".
   * Người làm khi **sửa** chỉ tính gợi ý của AI: chọn tay đã lưu ngay rồi.
   */
  const initialTypeId = task?.taskTypeId || types.data?.[0]?.id || '';
  const dirty =
    title.trim() !== (task?.title ?? '').trim() ||
    description.trim() !== (task?.description ?? '').trim() ||
    kind !== (task?.kind === 'milestone' ? 'milestone' : 'task') ||
    checklistDirty ||
    (taskTypeId !== '' && taskTypeId !== initialTypeId) ||
    (editing ? assigneeFromAi && assigneeId !== (task.assigneeId ?? '') : assigneeId !== '') ||
    priority !== (task?.priority ?? 'medium') ||
    estimate.trim() !==
      (task?.estimateHours === null || task?.estimateHours === undefined
        ? ''
        : String(task.estimateHours)) ||
    storyPoints.trim() !==
      (task?.storyPoints === null || task?.storyPoints === undefined
        ? ''
        : String(task.storyPoints)) ||
    startDate !== (task?.startDate ?? '') ||
    dueDate !== (task?.dueDate ?? '');
  const discard = useDiscardConfirm(dirty);
  // Mọi lối thoát do người dùng bấm (lớp phủ, Esc, X, Huỷ) đi qua đây; lưu xong thì đóng thẳng.
  const requestClose = (): void => discard.guard(onClose);

  const estimateHours = estimate.trim() === '' ? null : Number(estimate.replace(',', '.'));
  const warnings =
    standards.data === undefined
      ? []
      : checkTaskStandards(
          {
            estimateHours:
              kind === 'milestone' || Number.isNaN(estimateHours) ? null : estimateHours,
            startDate: startDate === '' ? null : startDate,
            dueDate: dueDate === '' ? null : dueDate,
            isSubtask: task?.parentId !== null && task?.parentId !== undefined,
          },
          standards.data,
        );

  const invalidate = async (): Promise<void> => {
    await queryClient.invalidateQueries({ queryKey: ['board', projectId] });
    await queryClient.invalidateQueries({ queryKey: ['gantt', projectId] });
    await queryClient.invalidateQueries({ queryKey: ['backlog', projectId] });
    await queryClient.invalidateQueries({ queryKey: ['me'] });
    await queryClient.invalidateQueries({ queryKey: ['task-checklist'] });
  };

  /**
   * Đổi người làm của một việc **đã có** — APM-TASK-assign.
   *
   * Đi đường riêng (`PATCH /tasks/:id/assignee`) chứ không gộp vào nút "Lưu":
   * đường ấy chạy phép kiểm quá tải và trả `warnings[]`, nên gộp lại thì một
   * cú bấm làm hai việc có hệ quả khác nhau, và cảnh báo quá tải lẫn vào cảnh
   * báo của phần còn lại. Lúc **tạo** thì không cần: người phụ trách đi kèm
   * payload tạo việc.
   */
  const assign = useMutation({
    mutationFn: async (userId: string) => {
      if (!editing) return null;
      return taskFormApi.assign(task.id, userId === '' ? null : userId);
    },
    onSuccess: async (result) => {
      if (result === null) return;
      await invalidate();
      for (const warning of result.warnings) toast.warning(warning.message);
      toast.success(t('taskForm.assigned'));
    },
    onError: (error) => toast.error(errorMessage(error, t)),
  });

  const save = useMutation({
    mutationFn: async () => {
      // Mốc không mang giờ ước lượng: nó là một điểm, không phải khối công việc.
      // Đổi một việc thành mốc là xoá giờ cũ — để lại thì nó vẫn ăn vào tải.
      const hours = kind === 'milestone' || Number.isNaN(estimateHours) ? null : estimateHours;
      const points =
        storyPoints.trim() === '' || !Number.isInteger(Number(storyPoints))
          ? null
          : Number(storyPoints);

      if (editing) {
        // Gợi ý người làm của Auto Assign: giao trước, rồi lưu phần còn lại.
        const assigned =
          assigneeFromAi && assigneeId !== (task.assigneeId ?? '')
            ? await taskFormApi.assign(task.id, assigneeId === '' ? null : assigneeId)
            : null;
        const updated = await taskFormApi.update(task.id, {
          ...(checklistDirty
            ? { checklist: { items: checklist ?? [], version: checklistVersion } }
            : {}),
          title: title.trim(),
          description: description.trim() === '' ? null : description.trim(),
          // Mốc không có loại lẫn ưu tiên (APM-TASK-ms2b) — không gửi; lệnh tự
          // xoá loại khi việc thành mốc.
          ...(kind === 'milestone' ? {} : { taskTypeId, priority }),
          /*
            Chỉ gửi khi người dùng **đổi**: task đang là `phase_gate` mở form ra
            sẽ thấy ô rơi về "Việc thường" (ô chọn không có giá trị ấy), và gửi
            vô điều kiện sẽ âm thầm biến một cổng pha thành việc thường chỉ vì
            ai đó bấm Lưu sau khi sửa tiêu đề.
          */
          ...(kind === (task.kind === 'milestone' ? 'milestone' : 'task') ? {} : { kind }),
          estimateHours: hours,
          // Chỉ gửi khi đổi — cùng lý do với `kind` ở trên.
          ...(points === (task.storyPoints ?? null) ? {} : { storyPoints: points }),
          startDate: startDate === '' ? null : startDate,
          dueDate: dueDate === '' ? null : dueDate,
        });
        return assigned === null
          ? updated
          : { ...updated, warnings: [...assigned.warnings, ...updated.warnings] };
      }

      return taskFormApi.create(projectId, {
        ...(checklist ? { checklist } : {}),
        title: title.trim(),
        ...(kind === 'milestone' ? {} : { taskTypeId, priority }),
        ...(kind === 'task' ? {} : { kind }),
        ...(description.trim() === '' ? {} : { description: description.trim() }),
        ...(statusId === null ? {} : { statusId }),
        ...(assigneeId === '' ? {} : { assigneeId }),
        ...(hours === null ? {} : { estimateHours: hours }),
        ...(points === null ? {} : { storyPoints: points }),
        ...(startDate === '' ? {} : { startDate }),
        ...(dueDate === '' ? {} : { dueDate }),
      });
    },
    onSuccess: async (result) => {
      await invalidate();
      /**
       * Cảnh báo của server hiện thành toast **riêng từng cái**, sau khi lưu.
       *
       * Không chặn (APM-1308a): task đã tạo xong, đây là lời nhắc chứ không
       * phải lỗi. Gộp thành một dòng thì hai cảnh báo khác nhau đọc như một.
       */
      for (const warning of result.warnings) toast.warning(warning.message);
      toast.success(editing ? t('taskForm.updated') : t('taskForm.created'));
      // Thẻ mới trên board tự cuộn tới và đáp xuống khi mọc ra — APM-BOARD-fx.
      if (!editing) announceCreated(result.task.id);
      onSaved?.(result.task);
      onClose();
    },
    onError: (error) => toast.error(errorMessage(error, t)),
  });

  const ask = useMutation({
    mutationFn: () =>
      track(
        {
          skill: 'task_breakdown',
          label: t('taskForm.breakdown'),
          describe: (result: AiSuggestionView) => {
            const parsed = BreakdownOutput.safeParse(result.content);
            return parsed.success
              ? t('taskForm.breakdownFound', { count: parsed.data.subtasks.length })
              : t('taskForm.breakdownEmpty');
          },
        },
        (runId) =>
          taskFormApi.askBreakdown(projectId, task!.id, t('taskForm.breakdownPrompt'), runId),
      ),
    onSuccess: (result) => {
      setSuggestion(result);
      const parsed = BreakdownOutput.safeParse(result.content);
      setChosen(new Set(parsed.success ? parsed.data.subtasks.map((_, index) => index) : []));
    },
    onError: (error) => toast.error(errorMessage(error, t)),
  });

  const accept = useMutation({
    mutationFn: () =>
      taskFormApi.acceptBreakdown(
        task!.id,
        suggestion!.id,
        [...chosen].sort((a, b) => a - b),
      ),
    onSuccess: async (created) => {
      setSuggestion(null);
      await invalidate();
      toast.success(t('taskForm.breakdownAccepted', { count: created.length }));
    },
    onError: (error) => toast.error(errorMessage(error, t)),
  });

  const plan = suggestion === null ? null : BreakdownOutput.safeParse(suggestion.content);
  const subtasks = plan?.success ? plan.data.subtasks : [];

  useEffect(() => {
    const onKey = (event: KeyboardEvent): void => {
      if (event.key === 'Escape') discard.guard(onClose);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose, discard.guard]);

  /**
   * Một ô người, hai chỗ đặt (dòng mốc và dòng "ai làm, bao lâu").
   *
   * ⚠️ Hàm trả phần tử, **không** phải component: khai `const Field = () => …`
   * trong thân render rồi viết `<Field />` là mỗi lần render một kiểu component
   * mới, React gỡ ra dựng lại, và danh sách chọn người đang mở tự đóng.
   */
  const assigneeField = (label?: string): ReactNode => (
    <AssigneePicker
      members={members.data ?? []}
      value={assigneeId}
      disabled={assign.isPending}
      {...(label === undefined ? {} : { label })}
      onChange={(userId) => {
        setAssigneeId(userId);
        // Đổi người làm thì giờ theo SP đổi theo tốc độ người mới (chưa gõ giờ tay).
        if (!estimateTouched) {
          const next = autoEstimate(storyPoints, userId);
          if (next !== null) setEstimate(next);
        }
        // Chọn tay là giao ngay; gợi ý AI trước đó (nếu có) thôi chờ nút Lưu.
        setAssigneeFromAi(false);
        if (editing) assign.mutate(userId);
      }}
    />
  );

  /**
   * Ô "Người làm" **chỉ đọc** cho người không có `task:assign` — APM-TASK-self.
   *
   * Server tự giao việc mới cho chính người tạo; không nói ra thì người dùng
   * tưởng việc của mình đang nằm chờ ai đó nhận. Sửa việc có sẵn thì hiện ai
   * đang làm, vì đổi người là việc của PM.
   */
  const assigneeReadonly = (label?: string): ReactNode => {
    const current = editing
      ? (members.data ?? []).find((member) => member.userId === task.assigneeId)
      : undefined;
    return (
      <div className="flex flex-col gap-1 text-xs">
        <span>{label ?? t('taskForm.assignee')}</span>
        <p className="flex h-9 items-center rounded-DEFAULT border border-dashed border-input px-2 text-sm text-muted-foreground">
          {editing
            ? (current?.displayName ?? t('taskForm.unassigned'))
            : t('taskForm.selfAssigned')}
        </p>
        <span className="text-[0.6875rem] text-muted-foreground">{t('taskForm.assigneeByPm')}</span>
      </div>
    );
  };

  return (
    <>
      <div className="fixed inset-0 z-40 popup-scrim" onClick={requestClose} aria-hidden />

      <div
        role="dialog"
        aria-modal="true"
        aria-label={editing ? t('taskForm.editTitle') : t('taskForm.createTitle')}
        className="fixed left-1/2 top-1/2 z-50 flex max-h-[85vh] w-full max-w-2xl -translate-x-1/2 -translate-y-1/2 flex-col overflow-y-auto rounded-DEFAULT border border-border bg-background p-4 shadow-xl glass-popup-2xl on-scrim"
      >
        <header className="mb-3 flex items-start gap-3">
          <div className="min-w-0 flex-1">
            <h2 className="text-base font-semibold">
              {editing ? t('taskForm.editTitle') : t('taskForm.createTitle')}
            </h2>
            {editing && <p className="font-mono text-xs text-muted-foreground">{task.key}</p>}
          </div>

          {/* Sheet iOS 26: ✓ là Lưu, ✕ là Huỷ — chân form không còn cặp nút chữ ấy.
              ✕ ở mép phải như mọi hộp (`Dialog`, 2026-10-05). */}
          <div className="flex items-center gap-2">
            <GlassIconButton
              variant="apply"
              label={t('taskForm.save')}
              pending={save.isPending}
              disabled={
                title.trim() === '' ||
                (kind !== 'milestone' && taskTypeId === '') ||
                !checklistValid ||
                save.isPending
              }
              onClick={() => save.mutate()}
            />
            <GlassIconButton variant="close" onClick={requestClose} label={t('common.close')} />
          </div>
        </header>

        <div className="flex flex-col gap-3">
          {/*
            Ô tích "Milestone" đứng **trước** tiêu đề — người dùng chốt 2026-09-25,
            thay cho ô chọn "Hạng mục: Việc thường / Milestone". Hai lựa chọn mà
            một cái là mặc định thì là một công tắc, không phải một danh sách.

            Vẫn **cạnh** loại việc, không thay nó — APM-TASK-f: `kind` là hình
            học của lịch (mốc không có thời lượng, Gantt vẽ hình thoi), còn loại
            việc là nhãn nghiệp vụ. Một mốc bàn giao vẫn cần phân loại được.
          */}
          <div className="flex items-end gap-3">
            <label className="flex h-9 shrink-0 items-center gap-1.5 text-sm">
              <input
                type="checkbox"
                checked={kind === 'milestone'}
                onChange={(event) => {
                  const next: TaskKind = event.target.checked ? 'milestone' : 'task';
                  setKind(next);
                  /*
                    Tích mốc thì gộp hai ngày làm một ngay tại chỗ, không đợi lúc
                    lưu: người dùng vừa thấy hai ô biến thành một, và nếu giá trị
                    bên dưới vẫn là hai ngày khác nhau thì thứ họ nhìn thấy không
                    phải thứ sẽ được lưu.
                  */
                  if (next === 'milestone') setStartDate(dueDate);
                }}
                className="ios-switch"
              />
              {t('taskForm.kindMilestone')}
            </label>
            <label className="flex min-w-0 flex-1 flex-col gap-1 text-xs">
              {t('taskForm.title')}
              <input
                value={title}
                autoFocus
                onChange={(event) => setTitle(event.target.value)}
                placeholder={t('taskForm.titlePlaceholder')}
                className="h-9 rounded-DEFAULT border border-input bg-background px-3 text-sm"
              />
            </label>
          </div>

          {editing ? null : (
            <TriageStrip
              projectId={projectId}
              title={title}
              description={description}
              onApply={(patch) => {
                if (patch.priority) setPriority(patch.priority);
                if (patch.assigneeId) setAssigneeId(patch.assigneeId);
              }}
            />
          )}

          <div className="flex flex-col gap-1 text-xs">
            <span>{t('taskForm.description')}</span>
            <RichTextEditor
              value={description}
              onChange={setDescription}
              label={t('taskForm.description')}
              placeholder={t('taskForm.descriptionPlaceholder')}
              toolbarExtra={
                <button
                  type="button"
                  title={t(
                    (task?.subtaskCount ?? 0) > 0 ? 'checklist.parentHelp' : 'checklist.add',
                  )}
                  aria-label={t('checklist.add')}
                  disabled={checklist !== null || (task?.subtaskCount ?? 0) > 0}
                  onClick={() => changeChecklist([{ id: randomUuid(), text: '', weight: null }])}
                  className="inline-flex items-center gap-1 rounded-sm px-2 py-1 text-xs hover:bg-muted disabled:opacity-40"
                >
                  <ListChecks className="size-4" />
                  {t('checklist.title')}
                </button>
              }
            >
              {checklist && <TaskChecklistEditor items={checklist} onChange={changeChecklist} />}
            </RichTextEditor>
          </div>

          {/*
            Ô "Người làm" có ở **cả** form tạo lẫn form sửa — APM-TASK-assign.

            Tạo: giá trị đi kèm payload tạo việc, một lượt gọi.
            Sửa: đổi người gọi thẳng `PATCH /tasks/:id/assignee` ngay khi chọn,
            chứ không đợi bấm "Lưu" — xem docblock của `assign` ở trên để biết vì
            sao hai việc ấy không gộp được.
          */}
          {kind === 'milestone' ? (
            /*
              Mốc: một dòng, người chịu trách nhiệm + ngày mốc.

              Mốc không có loại việc, ưu tiên hay giờ — APM-TASK-ms2b: nó là một
              điểm, không tốn công. Nó không có người **làm** — công sức nằm ở
              các việc dẫn tới nó — mà có người **chịu trách nhiệm**: người xác
              nhận mốc đã đạt và bị hỏi khi mốc trễ. Cùng một cột `assignee_id`.

              Mốc có **một** ngày, không phải một khoảng — APM-TASK-f. Theo chuẩn
              (PMBOK, MS Project, Primavera) mốc là sự kiện thời lượng bằng 0:
              `Start = Finish`, nên Gantt vẽ nó thành hình thoi chứ không phải
              thanh.

              ⚠️ Vẫn ghi vào **cả hai** cột, cùng một giá trị. `rangeOf` của Gantt
              trả `null` khi thiếu một trong hai, nên một mốc chỉ có ngày kết
              thúc sẽ **không vẽ ra gì** — biến mất khỏi đúng biểu đồ mà nó tồn
              tại để xuất hiện trên đó.
            */
            <div className="flex flex-wrap gap-3">
              <div className="w-64 max-w-full">
                {canAssign
                  ? assigneeField(t('taskForm.milestoneOwner'))
                  : assigneeReadonly(t('taskForm.milestoneOwner'))}
              </div>

              <label className="flex flex-col gap-1 text-xs">
                {t('taskForm.milestoneDate')}
                <DatePicker
                  value={dueDate}
                  disabled={scheduleLocked}
                  onChange={(value) => {
                    setDueDate(value);
                    setStartDate(value);
                  }}
                />
              </label>
            </div>
          ) : (
            /*
              Việc thường: ba dòng — người dùng chốt 2026-09-25.

              1. **Loại, ưu tiên** — việc này là gì, gấp tới đâu.
              2. **Ai làm, bao lâu** — người trước, giờ sau: số giờ là của người
                 ấy, đổi người thì thường phải ước lại.
              3. **Bắt đầu, hạn chót** — luôn chung một dòng (`flex`, không
                 `flex-wrap`): hai đầu của một khoảng đọc liền nhau, và ô hạn
                 chót mang `min` là ngày bắt đầu.
            */
            <>
              <div className="flex flex-wrap gap-3">
                <label className="flex flex-col gap-1 text-xs">
                  {t('taskForm.type')}
                  <select
                    value={taskTypeId}
                    onChange={(event) => setTaskTypeId(event.target.value)}
                    className="h-9 rounded-DEFAULT border border-input bg-background px-2 text-sm"
                  >
                    {(types.data ?? []).map((type) => (
                      <option key={type.id} value={type.id}>
                        {type.name}
                      </option>
                    ))}
                  </select>
                </label>

                <label className="flex flex-col gap-1 text-xs">
                  {t('taskForm.priority')}
                  <select
                    value={priority}
                    disabled={priorityLocked}
                    title={priorityLocked ? t('taskForm.priorityLocked') : undefined}
                    onChange={(event) => setPriority(event.target.value as typeof priority)}
                    className="h-9 rounded-DEFAULT border border-input bg-background px-2 text-sm disabled:opacity-60"
                  >
                    {TaskPriority.options.map((value) => (
                      <option key={value} value={value}>
                        {t(`taskForm.priorityName.${value}`)}
                      </option>
                    ))}
                  </select>
                </label>

                {canPlanWithAi && (
                  <label className="flex h-9 items-center gap-2 self-end text-sm">
                    <input
                      type="checkbox"
                      className="ios-switch"
                      checked={aiAssist}
                      onChange={(event) => setAiAssist(event.target.checked)}
                    />
                    {t('taskForm.ai.toggle')}
                  </label>
                )}
              </div>

              {/* Ngay dưới "Loại · Ưu tiên", **trên** "Người làm · Thời gian làm" — người
                  dùng chốt lại 2026-09-28 (trước đó 2026-09-25 nó nằm dưới hàng ấy): tích
                  "Dùng AI giao việc" thì story point hiện liền dưới ô vừa tích, và Auto
                  Assign điền xuống các hàng phía dưới nó. */}
              {canPlanWithAi && aiAssist && (
                <TaskFormAiAssist
                  projectId={projectId}
                  title={title}
                  description={description}
                  checklist={(checklist ?? []).map((item) => item.text)}
                  taskTypeId={taskTypeId}
                  priority={priority}
                  storyPoints={storyPoints}
                  onStoryPoints={changeStoryPoints}
                  hours={estimate}
                  onAssigned={(result) => {
                    setAssigneeId(result.assigneeId ?? '');
                    // Đang sửa: chờ nút Lưu mới giao (luật #6). Đang tạo: đi kèm payload.
                    setAssigneeFromAi(editing);
                    if (result.estimateHours !== null) {
                      setEstimate(String(result.estimateHours));
                      // Giờ của Auto Assign đã theo tốc độ người được chọn — không ghi đè.
                      setEstimateTouched(true);
                    }
                    if (result.startDate !== null) setStartDate(result.startDate);
                    if (result.dueDate !== null) setDueDate(result.dueDate);
                  }}
                />
              )}

              <div className="flex flex-wrap gap-3">
                <div className="w-64 max-w-full">
                  {canAssign ? assigneeField() : assigneeReadonly()}
                </div>

                <label className="flex flex-col gap-1 text-xs">
                  {t('taskForm.estimate')}
                  <input
                    type="number"
                    min={0}
                    step={1}
                    value={estimate}
                    onChange={(event) => {
                      setEstimate(event.target.value);
                      setEstimateTouched(true);
                    }}
                    className="h-9 w-28 rounded-DEFAULT border border-input bg-background px-2 text-right text-sm tabular-nums"
                  />
                  {estimateHint ? (
                    <span
                      className="max-w-56 text-[11px] text-muted-foreground"
                      data-testid="task-form-estimate-hint"
                    >
                      {estimateHint}
                    </span>
                  ) : null}
                </label>
              </div>

              <div className="flex gap-3">
                <label className="flex min-w-0 flex-1 flex-col gap-1 text-xs">
                  {t('taskForm.startDate')}
                  <DatePicker value={startDate} disabled={scheduleLocked} onChange={setStartDate} />
                </label>

                <label className="flex min-w-0 flex-1 flex-col gap-1 text-xs">
                  {t('taskForm.dueDate')}
                  <DatePicker
                    value={dueDate}
                    disabled={scheduleLocked}
                    onChange={setDueDate}
                    min={startDate || undefined}
                  />
                </label>
              </div>
            </>
          )}

          {/* Ô bị khoá phải nói vì sao — không thì trông như form hỏng. */}
          {scheduleLocked ? (
            <p className="text-xs text-muted-foreground">{t('taskForm.scheduleLocked')}</p>
          ) : priorityLocked && kind !== 'milestone' ? (
            <p className="text-xs text-muted-foreground">{t('taskForm.priorityLocked')}</p>
          ) : null}

          {/*
            Cảnh báo sống: hiện ngay lúc gõ, và **không** chặn nút Lưu. Quy chuẩn
            sai trong 5% trường hợp là chuyện thường; chặn thì người dùng nhập số
            giả để đi qua, và ước lượng giả là thứ nuôi velocity (APM-1308a).
          */}
          {warnings.length > 0 && (
            <ul className="flex flex-col gap-1 rounded-DEFAULT border border-rag-amber/40 bg-rag-amber/5 p-3 text-xs">
              {warnings.map((warning) => (
                <li key={warning.code}>{warning.message}</li>
              ))}
            </ul>
          )}

          {/*
            Phụ thuộc chỉ hiện khi **đang sửa**: một task chưa tồn tại thì chưa
            có id để mà nối vào, và server nhận cạnh theo id chứ không theo một
            danh sách chờ. Tạo xong mở lại là thêm được.
          */}
          {editing ? <TaskDependencyEditor projectId={projectId} task={task} /> : null}

          <div className="flex flex-wrap items-center gap-2 empty:hidden">
            {editing && (
              <Button
                variant="outline"
                className="ml-auto"
                disabled={ask.isPending}
                onClick={() =>
                  aiConfirm.confirm('task_breakdown', t('taskForm.breakdown'), () => ask.mutate())
                }
              >
                <Wand2 aria-hidden className="mr-1 size-4" />
                {ask.isPending ? t('taskForm.breakdownAsking') : t('taskForm.breakdown')}
              </Button>
            )}
            {aiConfirm.dialog}
          </div>

          {suggestion !== null && (
            <section className="flex flex-col gap-2 rounded-DEFAULT border border-border p-3">
              <h3 className="text-sm font-medium">{t('taskForm.breakdownTitle')}</h3>
              <PartialSuggestionNotice suggestion={suggestion} />

              {subtasks.length === 0 ? (
                <p className="text-xs text-muted-foreground">{t('taskForm.breakdownEmpty')}</p>
              ) : (
                <>
                  <ul className="flex flex-col gap-1">
                    {subtasks.map((subtask, index) => (
                      <li
                        key={`${subtask.title}-${index}`}
                        className="flex items-start gap-2 rounded-DEFAULT border border-border px-3 py-2 text-sm"
                      >
                        <input
                          type="checkbox"
                          className="ios-check ios-check-select"
                          checked={chosen.has(index)}
                          aria-label={subtask.title}
                          onChange={(event) =>
                            setChosen((current) => {
                              const next = new Set(current);
                              if (event.target.checked) next.add(index);
                              else next.delete(index);
                              return next;
                            })
                          }
                        />
                        <div className="min-w-0 flex-1">
                          <div className="flex flex-wrap items-center gap-2">
                            <span className="font-medium">{subtask.title}</span>
                            <span className="text-xs tabular-nums text-muted-foreground">
                              {t('taskForm.hours', { hours: subtask.estimateHours })}
                            </span>
                          </div>
                          <p className="text-xs text-muted-foreground">{subtask.reason}</p>
                          {/* Căn cứ của con số, khi trợ lý có gắn — APM-AI-y. */}
                          {subtask.estimate ? (
                            <EstimateCard estimate={subtask.estimate} className="mt-1" />
                          ) : null}
                        </div>
                      </li>
                    ))}
                  </ul>

                  <div className="flex flex-wrap items-center gap-2">
                    <Button
                      size="sm"
                      disabled={!canApprove || chosen.size === 0 || accept.isPending}
                      title={canApprove ? undefined : t('taskForm.needApprove')}
                      onClick={() => accept.mutate()}
                    >
                      {t('taskForm.breakdownApply', { count: chosen.size })}
                    </Button>
                    <Button size="sm" variant="outline" onClick={() => setSuggestion(null)}>
                      {t('taskForm.breakdownDiscard')}
                    </Button>
                  </div>
                </>
              )}
            </section>
          )}
        </div>
      </div>

      {discard.dialog}
    </>
  );
}
